"use client";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAudienceSession } from "@/contexts/audience-session-context";
import { getFeedbackMessageOverride } from "@/lib/feedback-message";
import { useViewerLanguage } from "@/lib/i18n";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { EmailInput } from "@/components/ui/email-input";
import { CircleCheck, Quote } from "lucide-react";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { useId, useState } from "react";
import RangeSlider from "../range-slider";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { primaryToneVar } from "./module-accent";

interface FeedbackState {
  email: string;
  understanding: number;
  actionability: number;
  relevance: number;
  overallRating: number;
  freeText: string;
}

export default function ModuleFeedback({
  grayscale,
  accent,
}: {
  grayscale?: boolean;
  accent?: number;
} = {}) {
  const emailId = useId();
  const { t } = useViewerLanguage();
  const tone = primaryToneVar(grayscale, accent);
  const { existingFeedback, presenterProfile, presentationId } =
    useAudienceSession();
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(!!existingFeedback);

  const [feedbackData, setFeedbackData] = useState<FeedbackState>({
    email: "",
    understanding: 0,
    actionability: 0,
    relevance: 0,
    overallRating: 0,
    freeText: "",
  });
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  const {
    email,
    understanding,
    actionability,
    relevance,
    overallRating,
    freeText,
  } = feedbackData;

  const handleSubmit = async () => {
    setSubmissionStatus("submitting");
    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          understanding,
          actionability,
          relevance,
          overall_rating: overallRating,
          free_text: freeText,
          presentation_id: presentationId,
        }),
      });

      if (response.status === 409) {
        setHasSubmitted(true);
        setSubmissionStatus("idle");
        return;
      }

      if (!response.ok) {
        console.error("Error submitting feedback:", response.statusText);
        setSubmissionError(
          t("There was an error submitting your feedback. Please try again later."),
        );
        setSubmissionStatus("error");
        return;
      }

      const result = await response.json();
      console.log("Feedback submitted successfully:", result.data);
      setSubmissionStatus("success");
      setHasSubmitted(true);
      setFeedbackData({
        email: "",
        understanding: 0,
        actionability: 0,
        relevance: 0,
        overallRating: 0,
        freeText: "",
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmissionError(
        t("There was an error submitting your feedback. Please try again later."),
      );
      setSubmissionStatus("error");
    }
  };

  if (submissionStatus === "success" || hasSubmitted) {
    const justSubmitted = submissionStatus === "success";
    const firstName = presenterProfile?.first_name?.trim() || null;
    const presenterName =
      [presenterProfile?.first_name, presenterProfile?.last_name]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(" ") || null;
    const presenterRole = presenterProfile?.role?.trim() || null;
    const avatarUrl = presenterProfile?.avatar_url || null;
    const thankYouMessage =
      presenterProfile?.feedback_thank_you_message?.trim() ||
      getFeedbackMessageOverride() ||
      t("Thanks for your feedback — every response makes the next talk sharper.");

    return (
      <div className="flex flex-1 flex-col justify-center gap-8 py-6">
        <div className="flex flex-col gap-2">
          <AnimatedIcon
            name="circle-check"
            fallback={CircleCheck}
            className="size-6 text-primary"
            style={tone ? { color: tone } : undefined}
          />
          <h2
            className={`text-xl font-medium text-neutral-900 dark:text-neutral-100 ${MODULE_LABEL_WRAP.body}`}
          >
            {justSubmitted
              ? t("Thanks, your feedback is in.")
              : t("You already shared feedback.")}
          </h2>
          <p
            className={`text-sm text-neutral-500 dark:text-neutral-400 ${MODULE_LABEL_WRAP.body}`}
          >
            {justSubmitted
              ? firstName
                ? t("It goes straight to {name}. Feedback can be shared once per talk.", {
                    name: firstName,
                  })
                : t("It goes straight to the presenter. Feedback can be shared once per talk.")
              : t("Thanks again. Feedback can be shared once per talk.")}
          </p>
        </div>

        <figure className="flex flex-col gap-4 border-t border-neutral-200 pt-6 dark:border-white/[0.08]">
          <Quote
            className="size-5 text-neutral-300 dark:text-neutral-600"
            aria-hidden
          />
          <blockquote
            className={`text-lg leading-snug text-neutral-900 dark:text-neutral-100 ${MODULE_LABEL_WRAP.body}`}
          >
            {thankYouMessage}
          </blockquote>
          {presenterName ? (
            <figcaption className="flex min-w-0 items-center gap-3">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={presenterName}
                  className="size-10 shrink-0 rounded-full object-cover"
                />
              ) : null}
              <div className="flex min-w-0 flex-col">
                <span
                  className={`text-sm font-medium text-neutral-900 dark:text-neutral-100 ${MODULE_LABEL_WRAP.row}`}
                >
                  {presenterName}
                </span>
                {presenterRole ? (
                  <span
                    className={`text-sm text-neutral-500 dark:text-neutral-400 ${MODULE_LABEL_WRAP.row}`}
                  >
                    {presenterRole}
                  </span>
                ) : null}
              </div>
            </figcaption>
          ) : null}
        </figure>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="flex flex-col gap-6 relative">
        <div className="">
          <RangeSlider
            min={0}
            max={10}
            snap={true}
            value={understanding}
            onChange={(val) =>
              setFeedbackData({ ...feedbackData, understanding: val })
            }
            label={t("Understanding")}
          />
          <RangeSlider
            min={0}
            max={10}
            snap={true}
            value={actionability}
            onChange={(val) =>
              setFeedbackData({ ...feedbackData, actionability: val })
            }
            label={t("Actionability")}
          />
          <RangeSlider
            min={0}
            max={10}
            snap={true}
            value={relevance}
            onChange={(val) =>
              setFeedbackData({ ...feedbackData, relevance: val })
            }
            label={t("Relevance")}
          />
          <RangeSlider
            min={0}
            max={10}
            snap={true}
            value={overallRating}
            onChange={(val) =>
              setFeedbackData({ ...feedbackData, overallRating: val })
            }
            label={t("Overall Rating")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label
            htmlFor="freeText"
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t("Additional Feedback (optional)")}
          </Label>
          <Textarea
            id="freeText"
            value={freeText}
            onChange={(e) =>
              setFeedbackData({ ...feedbackData, freeText: e.target.value })
            }
            className="w-full rounded-md border border-gray-300 dark:border-white/[0.15] p-2 text-base md:text-base pointer-fine:text-xs"
            rows={4}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label
            htmlFor={emailId}
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t("Email (optional)")}
          </Label>
          <EmailInput
            id={emailId}
            onChange={(v) => setFeedbackData({ ...feedbackData, email: v })}
            value={email}
            placeholder={t("Enter your email")}
            className="w-full rounded-md border border-gray-300 dark:border-white/[0.15] p-2 text-base pointer-fine:text-xs bg-transparent appearance-none focus:outline-none focus:ring-0 focus:border-neutral-400 dark:focus:border-white/[0.35]"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={(e) => {
              emitModuleInteraction(e.currentTarget, "click", {
                kind: "control",
                id: "submit",
              });
              handleSubmit();
            }}
            className="self-stretch"
            disabled={
              !feedbackData.understanding ||
              !feedbackData.actionability ||
              !feedbackData.relevance ||
              !feedbackData.overallRating ||
              submissionStatus === "submitting"
            }
          >
            {t("Submit Feedback")}
          </Button>
          <p className="text-xs text-destructive">
            {submissionStatus === "error" && submissionError}
          </p>
        </div>
      </div>
    </div>
  );
}
